import { create } from 'zustand';

import type {
    RuntimeMode,
    TradeExecutionPhase,
    TradeExecutionStatus,
} from '@/features/wallet';

import type {
    ActivePosition,
    ClosedTrade,
    OrderType,
    PositionType,
} from './types';
import { exchangePnlPercent } from './utils/pnl';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_BET = 50;
const INITIAL_BET_REAL = 15;
const INITIAL_DEMO_BALANCE = 100;
const PULSE_DURATION_MS = 1500;
const DISPLAY_AMOUNT_PRECISION = 2;
const CENTS_PER_UNIT = 10 ** DISPLAY_AMOUNT_PRECISION;
/** Below a cent a funding gap cannot be deposited away and renders as
 *  "Need $0.00 more" — treat it as float dust, not a shortfall. Exported so the
 *  submit path applies the same threshold as the banner. */
export const MIN_MEANINGFUL_USD = 1 / CENTS_PER_UNIT;

// Module-scoped timer replaces the useRef from useChartPulse.
let pulseTimerId: ReturnType<typeof setTimeout> | null = null;

// ── Cent quantization: direction matters ─────────────────────────────────
// Balances arrive with USDC's full 6 decimals (useSpeedBalance ->
// formatUnits(raw, 6)), e.g. 13.715623. Every bet amount is quantized to cents,
// so the ONLY safe direction depends on what the value is:
//
//   - an amount or a CEILING  -> floor  (13.715623 -> 13.71)
//   - a FLOOR (minimum)       -> ceil   (13.715623 -> 13.72)
//   - a display string        -> round half-up
//
// Rounding half-up used to be applied to all three. On a ceiling that produced
// a cap ABOVE the real limit: MAX on a $13.715623 balance set betAmount to
// $13.72, which selectCanAfford then rejected, the shortfall banner reported as
// "Need $0.00 more" (0.0044 to 2dp), and useBetClampEffect could not repair —
// its setBetAmount(13.715623) rounded straight back up to the offending 13.72,
// so the equality guard short-circuited and Long/Short stayed disabled.

/** Round to cents for DISPLAY only. Half-up — never apply to a bound. */
function roundDisplayAmount(value: number): number {
    return Number(value.toFixed(DISPLAY_AMOUNT_PRECISION));
}

/** Largest whole cent <= `value`. Use for amounts and upper bounds.
 *  The `toFixed(6)` snap absorbs binary-float representation error before the
 *  floor — 13.71 * 100 is 1370.9999999999998, which would otherwise drop an
 *  already-exact cent by one. 6dp is USDC's own precision, so it removes the
 *  artifact without masking a genuine sub-cent remainder. */
function floorToCents(value: number): number {
    if (!Number.isFinite(value)) return value;
    return (
        Math.floor(Number((value * CENTS_PER_UNIT).toFixed(6))) / CENTS_PER_UNIT
    );
}

/** Smallest whole cent >= `value`. Use for lower bounds — a minimum rounded
 *  DOWN would accept an amount the real minimum rejects (the mirror of the
 *  ceiling bug above). */
function ceilToCents(value: number): number {
    if (!Number.isFinite(value)) return value;
    return (
        Math.ceil(Number((value * CENTS_PER_UNIT).toFixed(6))) / CENTS_PER_UNIT
    );
}

function clampBetAmount(
    amount: number,
    minCollateralUsd: number | null,
    maxCollateralUsd: number | null,
): number {
    const normalizedAmount = floorToCents(Math.max(0, amount));
    if (normalizedAmount === 0) {
        return 0;
    }

    const safeMin =
        minCollateralUsd !== null
            ? ceilToCents(Math.max(0, minCollateralUsd))
            : null;
    const safeMax =
        maxCollateralUsd !== null
            ? floorToCents(Math.max(0, maxCollateralUsd))
            : null;

    if (safeMax !== null && normalizedAmount > safeMax) {
        return safeMax;
    }

    if (safeMin !== null && safeMax !== null && safeMin > safeMax) {
        return safeMax;
    }

    if (safeMin !== null && normalizedAmount < safeMin) {
        return safeMin;
    }

    return normalizedAmount;
}

/** The cap the bet input may not exceed, quantized DOWN to a whole cent.
 *  Quantizing here — at the one place the constraint is derived — is what keeps
 *  every consumer honest: the `above_max` check, the "Maximum Nx collateral is
 *  $X" copy, and useBetClampEffect's repair all compare cent-against-cent, so
 *  the message can never name a number the check itself rejects. */
function getEffectiveMaxCollateral(
    balanceForInputCap: number,
    leverageMaxCollateralUsd: number | null,
): number | null {
    if (balanceForInputCap === Infinity) {
        return leverageMaxCollateralUsd === null
            ? null
            : floorToCents(leverageMaxCollateralUsd);
    }

    if (leverageMaxCollateralUsd === null) {
        return floorToCents(balanceForInputCap);
    }

    return floorToCents(Math.min(balanceForInputCap, leverageMaxCollateralUsd));
}

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------

/** Exchange-sourced position figures. Mirrors the fields of LivePosition that
 *  any display surface needs; `markPrice` is derived, since clearinghouseState
 *  carries no per-position mark. */
export interface LivePositionSnapshot {
    entryPx: number | null;
    marginUsed: number | null;
    leverage: number | null;
    unrealizedPnl: number | null;
    returnOnEquity: number | null;
    markPrice: number | null;
    /** exchange position size, in asset units */
    size: number | null;
    isLong: boolean | null;
    /**
     * The feed price when this snapshot was taken. Captured HERE rather than
     * passed in, so it is always the price that was on screen at the instant the
     * exchange figures were adopted — that is what makes realtimePnl land exactly
     * on the exchange value at every poll.
     */
    anchorPrice: number | null;
    /**
     * Hyperliquid's own liquidation price for this market.
     *
     * Held per coin because with several positions open a user can be liquidated
     * on a market they are not looking at — the accepted cost of Option A, and
     * the reason the positions table needs a warning state rather than the chart
     * alone showing a liq line for the charted market.
     */
    liquidationPx: number | null;
}

/**
 * Everything this client knows about ONE market's position.
 *
 * The store used to hold exactly one of these, spread across a dozen flat
 * `exec*` fields, because the product only ever had one position. It always
 * could have had more: the backend's uniqueness constraint is
 * `{networkId, userId, pairBase, activeSlot}` — one active trade PER COIN,
 * unconstrained across coins. BTC and ETH open at once is a legal state that
 * the client used to treat as corruption.
 *
 * `positionsByCoin` is now the source of truth and the flat fields are a
 * PROJECTION of the selected market's slot (see `projectSelected`). That is not
 * a transitional shim — it is the Option A rule expressed in the state shape:
 * "the rail follows the chart", so every existing consumer of `executionStatus`
 * / `execEntryPrice` / … is already asking the right question and needs none.
 * Writers go through the map; readers keep reading the projection.
 */
export interface PositionSlot {
    status: TradeExecutionStatus;
    phase: TradeExecutionPhase | null;
    tradeHash: string | null;
    openTxHash: string | null;
    positionType: PositionType | null;
    positionAmount: number | null;
    entryPrice: number | null;
    leverage: number | null;
    /**
     * Set on limit-origin slots only (the price the GTC entry rests, or rested,
     * at). It is what the resting card reads while `status === 'resting'`, and
     * it survives the fill as the "this started as a limit order" marker.
     */
    limitPrice: string | null;
    openedAt: number | null;
    fillMs: number | null;
    openSource: 'live' | 'restored' | null;
    /** the exchange's view of THIS market, anchored for realtime extrapolation. */
    livePosition: LivePositionSnapshot | null;
}

export const IDLE_SLOT: PositionSlot = {
    status: 'idle',
    phase: null,
    tradeHash: null,
    openTxHash: null,
    positionType: null,
    positionAmount: null,
    entryPrice: null,
    leverage: null,
    limitPrice: null,
    openedAt: null,
    fillMs: null,
    openSource: null,
    livePosition: null,
};

/** A slot worth keeping in the map. Idle slots are dropped so the map stays the
 *  set of markets the user actually holds — which is what the aggregate reads
 *  (exposure, liquidation attention, "is anything open") depend on. */
function slotIsLive(slot: PositionSlot): boolean {
    return (
        slot.status !== 'idle' ||
        slot.tradeHash !== null ||
        slot.livePosition !== null
    );
}

/** Coin case is not consistent across the stack — the backend lowercases
 *  `pairBase`, Hyperliquid returns original case, the token-pair store carries
 *  whatever the market list gave it. Normalising once here is what stops a
 *  position from appearing twice under `btc` and `BTC`. */
export function coinKey(coin: string | null | undefined): string | null {
    const trimmed = coin?.trim();
    return trimmed ? trimmed.toUpperCase() : null;
}

/**
 * A demo-mode limit order. Real resting orders live on Hyperliquid and reach
 * the store as a `resting` slot; demo has no backend, so the order is kept here
 * and filled locally by `useDemoLimitFills` when the streamed price crosses it.
 *
 * Keyed by upper-cased coin, exactly like `positionsByCoin`.
 */
export interface DemoRestingOrder {
    type: PositionType;
    limitPrice: number;
    amount: number;
    leverage: number;
    placedAt: number;
}

interface TradingState {
    // ── Owned state ──────────────────────────────────────────────────────
    demoBalance: number;
    activePosition: ActivePosition | null;
    /** demo-mode resting limit orders, keyed by upper-cased coin. */
    demoRestingOrders: Record<string, DemoRestingOrder>;
    demoCancelledOrders: Array<
        DemoRestingOrder & { coin: string; cancelledAt: number }
    >;
    tradeHistory: ClosedTrade[];
    betAmount: number;
    leverage: number;
    /** True until the user explicitly picks a leverage — while true, the
     *  leverage rides each market's max (clamped by TradeConfigToggles). */
    leverageAuto: boolean;
    takeProfit: string;
    stopLoss: string;
    /** Slippage tolerance in basis points sent on open (default 100 = 1%). */
    slippageBps: number;
    /**
     * Market (IOC at mark ± slippage) or limit (GTC at a price the user picks).
     * Persisted with the rest of the trade config: it is a policy, not a draft.
     */
    orderType: OrderType;
    /**
     * The limit price the user typed, as TEXT so a half-entered decimal ("81."
     * or "") survives the round trip. NOT persisted and cleared on a pair
     * switch: a price is meaningful for exactly one market.
     */
    limitPrice: string;
    leverageMinCollateralUsd: number | null;
    leverageMaxCollateralUsd: number | null;
    overlayTrade: ClosedTrade | null;
    chartPulse: 'win' | 'loss' | null;
    pendingClosedTradeHash: string | null;

    // ── Synced state (written by useTradingStoreSync) ────────────────────
    runtimeMode: RuntimeMode;
    token: string | null;
    address: string | undefined;
    executionStatus: TradeExecutionStatus;
    executionPhase: TradeExecutionPhase | null;
    tradeHash: string | null;
    openTxHash: string | null;
    execPositionType: PositionType | null;
    execPositionAmount: number | null;
    execEntryPrice: number | null;
    execLeverage: number | null;
    /** Real open time (ms epoch) — see `TradeExecutionState.openedAt`. */
    execOpenedAt: number | null;
    /** Measured request→fill latency of a live open; null when restored. */
    execFillMs: number | null;
    /** `live` = opened in this session, `restored` = hydrated from the API. */
    execOpenSource: 'live' | 'restored' | null;
    /** the selected market's limit price (see `PositionSlot.limitPrice`). */
    execLimitPrice: string | null;
    balance: number;
    balanceForInputCap: number;
    custodialAvailable: number;
    custodialNumericBalance: number | null;
    isCustodialBalanceLoading: boolean;
    isCustodialBalanceSettling: boolean;
    /** balance read succeeded but trading authority has not resolved yet; render it dimmed. */
    isCustodialBalanceProvisional: boolean;
    /**
     * The exchange's view of the open position, straight from
     * clearinghouseState via GET /speed/position/:coin. Held in the store so
     * selectActiveTradeVisual can hand ONE set of numbers to the chart, the
     * positions table and the position panel.
     *
     * Every surface used to derive its own: the chart cursor recomputed from the
     * client's captured entry and requested collateral against the animated LAST
     * price, the table read the same store values, and the panel read the
     * exchange. On one live BTC trade that produced +$0.20 / +2.43% on the cursor
     * and +$0.27 / +3.29% everywhere else. Null in demo mode and before the
     * first read lands.
     */
    livePosition: LivePositionSnapshot | null;
    /**
     * Every market this client knows a position for, keyed by upper-cased coin.
     * The flat `exec*` / `tradeHash` / `livePosition` fields above are the
     * projection of `positionsByCoin[selectedCoin]`; write here, read there.
     */
    positionsByCoin: Record<string, PositionSlot>;
    /** the market on the chart, mirrored from useTokenPairStore. Taking the
     *  projection at this coin is what makes "the rail follows the chart" a
     *  property of the state rather than of each component. */
    selectedCoin: string | null;
    hasPairData: boolean;
    /** a Snappy Wallet reactivation (agent setup) is running; opening a position would fail
     *  against a half-approved agent, so every trade entry point is blocked meanwhile. */
    isReactivating: boolean;
    /** trade-status websocket is live, so status changes arrive by push. while this is
     *  false the active-trades query falls back to polling — see useActiveUserTrades. */
    tradeStreamConnected: boolean;
    resumeUnverified: boolean;
    currentValue: number;
    /**
     * The market `currentValue` belongs to. Null while the animated price is
     * still travelling from the market the user just left.
     *
     * `currentValue` is a spring over the price feed, so a market switch used to
     * leave it sweeping between two unrelated scales. Anchoring a position's PnL
     * at one of those in-between values produced the reported
     * -$27,855,852.53 (-279,189,353.66%) on a 5x LONG at $0.1406: the anchor had
     * been stamped at the PREVIOUS market's ~$78,467, so the first honest tick
     * measured a $78,000 "move" against the new market's position size.
     */
    currentValueCoin: string | null;
    /** Trading keyboard shortcuts (L/S/C/arrows); ⌘K is always on. */
    shortcutsEnabled: boolean;

    // ── Actions ──────────────────────────────────────────────────────────
    setBetAmount: (amount: number) => void;
    adjustBet: (delta: number) => void;
    setPercentage: (pct: number) => void;
    setDirectBetAmount: (amount: number) => void;
    setLeverage: (val: number) => void;
    /** User-initiated leverage pick: turns auto-max off and persists. */
    chooseLeverage: (val: number) => void;
    setTakeProfit: (val: string) => void;
    setStopLoss: (val: string) => void;
    setSlippageBps: (bps: number) => void;
    setOrderType: (type: OrderType) => void;
    setLimitPrice: (value: string) => void;
    setLeverageConstraints: (d: {
        minCollateralUsd: number | null;
        maxCollateralUsd: number | null;
    }) => void;
    clearLeverageConstraints: () => void;
    setDemoBalance: (fn: number | ((prev: number) => number)) => void;
    setActivePosition: (pos: ActivePosition | null) => void;
    placeDemoRestingOrder: (coin: string, order: DemoRestingOrder) => void;
    cancelDemoRestingOrder: (coin: string) => void;
    /** Turn a demo resting order into the demo position, at its own price. */
    fillDemoRestingOrder: (coin: string) => void;
    addTrade: (trade: ClosedTrade) => void;
    setOverlayTrade: (trade: ClosedTrade | null) => void;
    triggerPulse: (type: 'win' | 'loss') => void;
    setCurrentValue: (val: number, coin?: string | null) => void;
    setShortcutsEnabled: (enabled: boolean) => void;
    setPendingClosedTradeHash: (hash: string | null) => void;
    setTradeStreamConnected: (connected: boolean) => void;
    setResumeUnverified: (val: boolean) => void;
    resetResumeFlags: () => void;
    resetForModeSwitch: () => void;
    /**
     * V2 only: the browser wallet the session key named (`v2:<address>`) is gone. `_syncAuth` runs it in the same
     * write as the token change; see {@link walletScopedReset}.
     */
    resetForWalletChange: () => void;

    // ── Sync actions (prefixed _ to signal internal use) ─────────────────
    _syncAuth: (d: {
        runtimeMode: RuntimeMode;
        token: string | null;
        address: string | undefined;
    }) => void;
    _syncSelectedCoin: (coin: string | null) => void;
    /** Hydrate slots for active trades this client is not driving, so a position
     *  opened in another tab — or before a reload — is visible and closeable. */
    /** A trade reached a terminal status; drop the slot holding it. */
    _closeSlotForTrade: (tradeHash: string) => void;
    _syncActiveTrades: (
        trades: {
            coin: string;
            tradeHash: string;
            openTxHash: string | null;
            positionType: PositionType | null;
            positionAmount: number | null;
            entryPrice: number | null;
            leverage: number | null;
            /** absent on a market row; see `PositionSlot.limitPrice`. */
            limitPrice?: string | null;
            openedAt: number | null;
            status: TradeExecutionStatus;
        }[],
    ) => void;
    _syncExecution: (d: {
        coin: string | null;
        status: TradeExecutionStatus;
        phase: TradeExecutionPhase | null;
        tradeHash: string | null;
        openTxHash: string | null;
        positionType: PositionType | null;
        positionAmount: number | null;
        entryPrice: number | null;
        leverage: number | null;
        /** absent on a market open; see `PositionSlot.limitPrice`. */
        limitPrice?: string | null;
        openedAt: number | null;
        fillMs: number | null;
        openSource: 'live' | 'restored' | null;
    }) => void;
    _syncBalance: (d: {
        balance: number;
        balanceForInputCap: number;
        custodialAvailable: number;
    }) => void;
    _syncCustodialBalance: (d: {
        numericBalance: number | null;
        isLoading: boolean;
        isSettling: boolean;
        isProvisional?: boolean;
    }) => void;
    /** `anchorPrice` is stamped by the store, never supplied by the caller. */
    _syncLivePosition: (
        coin: string | null,
        snapshot: Omit<LivePositionSnapshot, 'anchorPrice'> | null,
    ) => void;
    _syncHasPairData: (hasPairData: boolean) => void;
    _syncIsReactivating: (isReactivating: boolean) => void;
}

// ---------------------------------------------------------------------------
// Persisted trade config — the user's chosen policy survives sessions.
// leverage null/absent = "auto": ride each market's max until the user
// explicitly picks a value (per Alex 2026-07-11). Defaults for newcomers:
// TP 100% armed, SL 50% armed (protection on by default), slippage 1%.
// ---------------------------------------------------------------------------

const TRADE_CONFIG_KEY = 'snappy.tradeConfig.v1';

/**
 * Leverage is whole-number only (per Alex 2026-09-04) — no 1.5×, no 19.9×.
 * The ladders in `utils/leverageOptions` only offer integers; this is the
 * last line of defence for anything else that reaches the store (a value
 * persisted by an older build, a typed input). null = not a usable leverage.
 */
function toWholeLeverage(val: number): number | null {
    if (!Number.isFinite(val) || val <= 0) return null;

    return Math.max(1, Math.round(val));
}

interface PersistedTradeConfig {
    leverage: number | null;
    takeProfit: string;
    stopLoss: string;
    slippageBps: number;
    /** Last bet the user actually had (>0); clamped to balance on load. */
    betAmount: number;
    /** 'market' | 'limit'. Absent (older builds) reads as 'market'. */
    orderType: OrderType;
}

function loadTradeConfig(): Partial<PersistedTradeConfig> {
    try {
        const raw = localStorage.getItem(TRADE_CONFIG_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object'
            ? (parsed as Partial<PersistedTradeConfig>)
            : {};
    } catch {
        return {};
    }
}

function persistTradeConfig(patch: Partial<PersistedTradeConfig>): void {
    try {
        localStorage.setItem(
            TRADE_CONFIG_KEY,
            JSON.stringify({ ...loadTradeConfig(), ...patch }),
        );
    } catch {
        // preference just won't persist
    }
}

const persistedConfig = loadTradeConfig();

const persistedBet =
    typeof persistedConfig.betAmount === 'number' &&
    persistedConfig.betAmount > 0
        ? persistedConfig.betAmount
        : null;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The map <-> projection bridge
// ---------------------------------------------------------------------------

/** The session key of the V2 (browser wallet) flow, `v2:<address>`; V1's is a JWT, never this. */
export function isV2SessionKey(token: string | null | undefined): boolean {
    return typeof token === 'string' && token.startsWith('v2:');
}

/** Immutably set one coin's slot, or drop it once it goes idle. */
function withSlot(
    map: Record<string, PositionSlot>,
    key: string,
    slot: PositionSlot,
): Record<string, PositionSlot> {
    if (!slotIsLive(slot)) {
        if (!(key in map)) return map;
        const next = { ...map };
        delete next[key];
        return next;
    }
    return { ...map, [key]: slot };
}

function sameSlotMap(
    a: Record<string, PositionSlot>,
    b: Record<string, PositionSlot>,
): boolean {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    return aKeys.every((k) => a[k] === b[k]);
}

/**
 * The flat fields every existing consumer reads, taken from ONE slot.
 *
 * This is the whole compatibility story of the multi-position migration: the
 * fifteen files that read `execEntryPrice` / `executionStatus` / `livePosition`
 * were all asking "what is the position on the market I am looking at", which is
 * exactly what this returns. They did not have to change when the store learned
 * to hold several.
 */
function projectFrom(
    positionsByCoin: Record<string, PositionSlot>,
    selectedCoin: string | null,
): Pick<
    TradingState,
    | 'positionsByCoin'
    | 'selectedCoin'
    | 'executionStatus'
    | 'executionPhase'
    | 'tradeHash'
    | 'openTxHash'
    | 'execPositionType'
    | 'execPositionAmount'
    | 'execEntryPrice'
    | 'execLeverage'
    | 'execOpenedAt'
    | 'execFillMs'
    | 'execOpenSource'
    | 'execLimitPrice'
    | 'livePosition'
> {
    const slot =
        (selectedCoin !== null ? positionsByCoin[selectedCoin] : undefined) ??
        IDLE_SLOT;
    return {
        positionsByCoin,
        selectedCoin,
        executionStatus: slot.status,
        executionPhase: slot.phase,
        tradeHash: slot.tradeHash,
        openTxHash: slot.openTxHash,
        execPositionType: slot.positionType,
        execPositionAmount: slot.positionAmount,
        execEntryPrice: slot.entryPrice,
        execLeverage: slot.leverage,
        execOpenedAt: slot.openedAt,
        execFillMs: slot.fillMs,
        execOpenSource: slot.openSource,
        execLimitPrice: slot.limitPrice,
        livePosition: slot.livePosition,
    };
}

/**
 * What a V2 wallet change drops: everything this store holds for the wallet that the session key named. Every
 * per-coin slot (the ones the execution hook drives AND the ones `_syncActiveTrades` hydrated, with their exchange
 * snapshots) and therefore the flat projection, the close still waiting for its history row, the overlay and pulse
 * of that wallet's last close, its history and the resume lock.
 *
 * `resetForModeSwitch` (V1 sign-in / sign-out, and the real → demo → real of a V2 wallet change) leaves
 * `positionsByCoin` to the execution reset, which only knows the slots it drives: a hydrated slot of the previous
 * wallet would survive into the next one and its card would close by coin on the next wallet's backend. V1 behaviour
 * is unchanged: this runs only when a `v2:` session key is left.
 */
function walletScopedReset(selectedCoin: string | null): Partial<TradingState> {
    if (pulseTimerId) {
        clearTimeout(pulseTimerId);
        pulseTimerId = null;
    }
    return {
        ...projectFrom({}, selectedCoin),
        pendingClosedTradeHash: null,
        overlayTrade: null,
        chartPulse: null,
        tradeHistory: [],
        resumeUnverified: false,
    };
}

export const useTradingStore = create<TradingState>((set, get) => ({
    // ── Owned state (initial values) ─────────────────────────────────────
    demoBalance: INITIAL_DEMO_BALANCE,
    activePosition: null,
    demoRestingOrders: {},
    demoCancelledOrders: [],
    tradeHistory: [],
    betAmount: persistedBet ?? INITIAL_BET,
    leverage:
        (typeof persistedConfig.leverage === 'number'
            ? toWholeLeverage(persistedConfig.leverage)
            : null) ?? 30,
    leverageAuto: typeof persistedConfig.leverage !== 'number',
    takeProfit:
        typeof persistedConfig.takeProfit === 'string'
            ? persistedConfig.takeProfit
            : '100%',
    stopLoss:
        typeof persistedConfig.stopLoss === 'string'
            ? persistedConfig.stopLoss
            : '50%',
    slippageBps:
        typeof persistedConfig.slippageBps === 'number'
            ? persistedConfig.slippageBps
            : 100,
    orderType: persistedConfig.orderType === 'limit' ? 'limit' : 'market',
    limitPrice: '',
    leverageMinCollateralUsd: null,
    leverageMaxCollateralUsd: null,
    overlayTrade: null,
    chartPulse: null,
    pendingClosedTradeHash: null,

    // ── Synced state (initial values) ────────────────────────────────────
    runtimeMode: 'demo',
    token: null,
    address: undefined,
    executionStatus: 'idle',
    executionPhase: null,
    tradeHash: null,
    openTxHash: null,
    execPositionType: null,
    execPositionAmount: null,
    execEntryPrice: null,
    execLeverage: null,
    execOpenedAt: null,
    execFillMs: null,
    execLimitPrice: null,
    execOpenSource: null,
    balance: INITIAL_DEMO_BALANCE,
    balanceForInputCap: INITIAL_DEMO_BALANCE,
    custodialAvailable: 0,
    custodialNumericBalance: null,
    isCustodialBalanceLoading: false,
    isCustodialBalanceSettling: false,
    isCustodialBalanceProvisional: false,
    livePosition: null,
    positionsByCoin: {},
    selectedCoin: null,
    hasPairData: false,
    isReactivating: false,
    // assume disconnected until the socket says otherwise, so a user who never gets a
    // socket keeps the old polling behaviour instead of silently going update-less.
    tradeStreamConnected: false,
    resumeUnverified: false,
    currentValue: 0,
    currentValueCoin: null,
    shortcutsEnabled:
        typeof window === 'undefined' ||
        window.localStorage.getItem('snappy.shortcuts.v1') !== 'off',

    // ── Actions ──────────────────────────────────────────────────────────

    setBetAmount: (amount) => {
        const nextAmount = floorToCents(Math.max(0, amount));
        if (get().betAmount !== nextAmount) {
            set({ betAmount: nextAmount });
        }
    },

    adjustBet: (delta) => {
        const {
            betAmount,
            balanceForInputCap,
            activePosition,
            leverageMinCollateralUsd,
            leverageMaxCollateralUsd,
        } = get();
        const next = betAmount + delta;
        const effectiveMax = activePosition
            ? 999999
            : getEffectiveMaxCollateral(
                  balanceForInputCap,
                  leverageMaxCollateralUsd,
              );
        const validAmount = clampBetAmount(
            next,
            activePosition ? null : leverageMinCollateralUsd,
            effectiveMax,
        );

        if (validAmount !== betAmount) {
            set({ betAmount: validAmount });
            if (validAmount > 0) persistTradeConfig({ betAmount: validAmount });
        }
    },

    setPercentage: (pct) => {
        const {
            balance,
            balanceForInputCap,
            leverageMinCollateralUsd,
            leverageMaxCollateralUsd,
        } = get();
        // floor, not round: MAX must never ask for more than the balance holds.
        const rawAmount = floorToCents(balance * pct);
        const validAmount = clampBetAmount(
            rawAmount,
            leverageMinCollateralUsd,
            getEffectiveMaxCollateral(
                balanceForInputCap,
                leverageMaxCollateralUsd,
            ),
        );
        if (validAmount !== get().betAmount) {
            set({ betAmount: validAmount });
            if (validAmount > 0) persistTradeConfig({ betAmount: validAmount });
        }
    },

    setDirectBetAmount: (amount) => {
        const { betAmount } = get();
        const validAmount = floorToCents(Math.max(0, amount));
        if (validAmount !== betAmount) {
            set({ betAmount: validAmount });
            if (validAmount > 0) persistTradeConfig({ betAmount: validAmount });
        }
    },

    setLeverage: (val) => {
        const whole = toWholeLeverage(val);
        if (whole !== null) {
            set({ leverage: whole });
        }
    },
    chooseLeverage: (val) => {
        const whole = toWholeLeverage(val);
        if (whole !== null) {
            set({ leverage: whole, leverageAuto: false });
            persistTradeConfig({ leverage: whole });
        }
    },
    setTakeProfit: (val) => {
        set({ takeProfit: val });
        persistTradeConfig({ takeProfit: val });
    },
    setStopLoss: (val) => {
        set({ stopLoss: val });
        persistTradeConfig({ stopLoss: val });
    },
    setSlippageBps: (bps) => {
        if (Number.isFinite(bps) && bps >= 0 && bps <= 5000) {
            set({ slippageBps: bps });
            persistTradeConfig({ slippageBps: bps });
        }
    },
    setOrderType: (type) => {
        if (get().orderType === type) return;
        set({ orderType: type });
        persistTradeConfig({ orderType: type });
    },
    setLimitPrice: (value) => {
        if (get().limitPrice === value) return;
        set({ limitPrice: value });
    },
    setLeverageConstraints: ({ minCollateralUsd, maxCollateralUsd }) =>
        set({
            leverageMinCollateralUsd:
                minCollateralUsd !== null && Number.isFinite(minCollateralUsd)
                    ? minCollateralUsd
                    : null,
            leverageMaxCollateralUsd:
                maxCollateralUsd !== null && Number.isFinite(maxCollateralUsd)
                    ? maxCollateralUsd
                    : null,
        }),
    clearLeverageConstraints: () =>
        set({
            leverageMinCollateralUsd: null,
            leverageMaxCollateralUsd: null,
        }),

    // Floored at 0 on purpose. Demo debits are sized against `balance`, which is
    // NOT always the demo balance (during the authority-resolving window it is
    // the real custodial number), so the sizing path and the balance source can
    // drift apart. A negative demo balance is never a legitimate state, so clamp
    // here as well as at the open path that refuses the oversized bet.
    setDemoBalance: (fn) =>
        set((state) => ({
            demoBalance: Math.max(
                0,
                typeof fn === 'function' ? fn(state.demoBalance) : fn,
            ),
        })),

    setActivePosition: (pos) => set({ activePosition: pos }),

    // The margin is debited when the order is PLACED, exactly as a demo market
    // open debits it — the money is committed the moment the order goes on the
    // book — and refunded in full if the order is cancelled unfilled.
    placeDemoRestingOrder: (coin, order) => {
        const key = coinKey(coin);
        if (key === null) return;
        set((state) => ({
            demoRestingOrders: { ...state.demoRestingOrders, [key]: order },
            demoBalance: Math.max(0, state.demoBalance - order.amount),
        }));
    },

    cancelDemoRestingOrder: (coin) => {
        const key = coinKey(coin);
        if (key === null) return;
        set((state) => {
            const order = state.demoRestingOrders[key];
            if (!order) return state;
            const next = { ...state.demoRestingOrders };
            delete next[key];
            return {
                demoRestingOrders: next,
                demoBalance: state.demoBalance + order.amount,
                demoCancelledOrders: [
                    ...state.demoCancelledOrders,
                    { ...order, coin: key, cancelledAt: Date.now() },
                ].slice(-20),
            };
        });
    },

    // A limit order fills AT ITS OWN PRICE, not at whatever tick crossed it —
    // that is the whole point of naming a price — so the entry is the limit.
    fillDemoRestingOrder: (coin) => {
        const key = coinKey(coin);
        if (key === null) return;
        set((state) => {
            const order = state.demoRestingOrders[key];
            // Demo holds ONE position at a time, so a filled order cannot land
            // on top of an existing one. It stays resting until that closes.
            if (!order || state.activePosition) return state;
            const next = { ...state.demoRestingOrders };
            delete next[key];
            return {
                demoRestingOrders: next,
                activePosition: {
                    type: order.type,
                    entryPrice: order.limitPrice,
                    limitPrice: order.limitPrice,
                    timestamp: Date.now(),
                    amount: order.amount,
                    leverage: order.leverage,
                },
            };
        });
    },

    addTrade: (trade) =>
        set((state) => ({
            tradeHistory: [
                ...state.tradeHistory,
                {
                    ...trade,
                    ...(state.activePosition?.limitPrice !== undefined
                        ? { limitPrice: state.activePosition.limitPrice }
                        : {}),
                },
            ],
        })),

    setOverlayTrade: (trade) => set({ overlayTrade: trade }),

    triggerPulse: (type) => {
        if (pulseTimerId) {
            clearTimeout(pulseTimerId);
        }
        set({ chartPulse: type });
        pulseTimerId = setTimeout(() => {
            set({ chartPulse: null });
            pulseTimerId = null;
        }, PULSE_DURATION_MS);
    },

    setCurrentValue: (val, coin) => {
        const key = coinKey(coin);
        if (get().currentValue === val && get().currentValueCoin === key)
            return;
        set({ currentValue: val, currentValueCoin: key });
    },

    setShortcutsEnabled: (enabled) => {
        set({ shortcutsEnabled: enabled });
        try {
            window.localStorage.setItem(
                'snappy.shortcuts.v1',
                enabled ? 'on' : 'off',
            );
        } catch {
            // storage unavailable — the preference just won't persist
        }
    },

    setPendingClosedTradeHash: (hash) => set({ pendingClosedTradeHash: hash }),

    setTradeStreamConnected: (connected) =>
        set({ tradeStreamConnected: connected }),

    setResumeUnverified: (val) => {
        if (get().resumeUnverified !== val) set({ resumeUnverified: val });
    },

    resetResumeFlags: () => {
        const s = get();
        if (s.resumeUnverified) {
            set({ resumeUnverified: false });
        }
    },

    resetForModeSwitch: () => {
        if (pulseTimerId) {
            clearTimeout(pulseTimerId);
            pulseTimerId = null;
        }
        const isDemoMode = get().runtimeMode === 'demo';
        // Mode switches keep the user's saved bet (clamped by the bet-clamp
        // effect once the new mode's balance lands); fall back to the mode
        // default only when nothing was ever saved.
        const savedBet = loadTradeConfig().betAmount;
        set({
            // A demo position cannot survive into a real session: it is priced
            // against whatever market is charted, so signing in with one open
            // would carry a play-money number into an account holding real
            // funds. Signing OUT drops it for the same reason in reverse.
            activePosition: null,
            demoRestingOrders: {},
            demoCancelledOrders: [],
            tradeHistory: [],
            overlayTrade: null,
            betAmount:
                typeof savedBet === 'number' && savedBet > 0
                    ? savedBet
                    : isDemoMode
                      ? INITIAL_BET
                      : INITIAL_BET_REAL,
            chartPulse: null,
            leverageMinCollateralUsd: null,
            leverageMaxCollateralUsd: null,
            pendingClosedTradeHash: null,
            // Reset in BOTH directions. Demo money is per-demo-session, and a
            // stale one is not inert: a $6,561 demo balance left in the store
            // was what the balance row fell back to while a real account was
            // still handshaking.
            demoBalance: INITIAL_DEMO_BALANCE,
        });
    },

    resetForWalletChange: () => {
        set(walletScopedReset(get().selectedCoin));
    },

    // ── Sync actions ─────────────────────────────────────────────────────
    // Thin setters with Object.is guards to avoid no-op updates.

    _syncAuth: (d) => {
        const s = get();
        if (
            s.runtimeMode === d.runtimeMode &&
            s.token === d.token &&
            s.address === d.address
        )
            return;
        // A V2 session key names ONE browser wallet. Leaving it (its lock on a wallet change or a forget, then
        // `v2:<B>`) drops what was held for it in the same write, so a store keyed `v2:<B>` never holds a slot of
        // `v2:<A>`. A V1 token change (a refresh, sign-in, sign-out) never matches.
        const leftV2Wallet = isV2SessionKey(s.token) && s.token !== d.token;
        set({
            runtimeMode: d.runtimeMode,
            token: d.token,
            address: d.address,
            ...(leftV2Wallet ? walletScopedReset(s.selectedCoin) : {}),
        });
    },

    _syncSelectedCoin: (coin) => {
        const key = coinKey(coin);
        if (get().selectedCoin === key) return;
        set(projectFrom(get().positionsByCoin, key));
    },

    /**
     * Clear the slot for a trade the exchange has finished with.
     *
     * The push that reports a stop-loss, take-profit or liquidation is routed to
     * a market by `coinForTradeHash`, which searches only the slots THIS tab is
     * driving. A position restored after a reload — or opened in another tab —
     * is not in that map, so the close was dropped and the position stayed on
     * the chart until the backstop list refetch noticed, or forever if that
     * request failed. The same handler already finds those trades for its close
     * TOAST, through the store; this makes the state change agree with it.
     *
     * Idempotent, and deliberately narrow: it only clears a slot whose hash
     * matches and which is not mid-flight locally, so it can never cancel an
     * open or a close this tab is still executing.
     */
    _closeSlotForTrade: (tradeHash) => {
        const s = get();
        const hit = findSlotByTradeHash(s, tradeHash);
        if (!hit) return;
        if (hit.slot.status === 'submitting' || hit.slot.status === 'closing') {
            return;
        }
        const next = { ...s.positionsByCoin };
        delete next[hit.coin];
        set(projectFrom(next, s.selectedCoin));
    },

    _syncActiveTrades: (trades) => {
        const s = get();
        // Only in real mode: a demo position lives in `activePosition` and has no
        // server row, so hydrating from an empty list would clear nothing and a
        // stale list could invent a slot the demo user never opened.
        if (s.runtimeMode !== 'real') return;

        const next: Record<string, PositionSlot> = {};
        let changed = false;

        for (const trade of trades) {
            const key = coinKey(trade.coin);
            if (key === null) continue;
            const existing = s.positionsByCoin[key];
            // A slot this client is actively driving wins: it holds the live
            // `fillMs` / `openSource: 'live'` and the in-flight phase, which the
            // server list cannot report. The list's job is to ADD the markets the
            // client knows nothing about, not to overwrite what it is executing.
            //
            // ...with one exception, and it is the whole reason a limit order
            // needs this: a limit row KEEPS its `hl:<oid>` hash across
            // Resting → Open, so matching on the hash alone would pin the rail
            // to the resting card forever over a position that is already live
            // (a fill that landed while the socket was down, or one restored
            // after a reload). When the list reports a DIFFERENT status than the
            // slot holds, the list is right — unless this client is mid-request,
            // where the list is simply behind.
            const midFlight =
                existing?.status === 'submitting' ||
                existing?.status === 'closing';
            if (
                existing &&
                existing.tradeHash === trade.tradeHash &&
                (midFlight || existing.status === trade.status)
            ) {
                next[key] = existing;
                continue;
            }
            const slot: PositionSlot = {
                ...IDLE_SLOT,
                status: trade.status,
                tradeHash: trade.tradeHash,
                openTxHash: trade.openTxHash,
                positionType: trade.positionType,
                positionAmount: trade.positionAmount,
                entryPrice: trade.entryPrice,
                leverage: trade.leverage,
                // kept across the Resting → Open flip: it is what marks the row
                // as limit-origin, and the list stops sending an entry-less row
                // the moment it fills.
                limitPrice: trade.limitPrice ?? existing?.limitPrice ?? null,
                openedAt: trade.openedAt ?? existing?.openedAt ?? null,
                openSource: 'restored',
                // carried over so a market's PnL does not blink to null on every
                // refresh of the list; the position poll owns this field.
                livePosition: existing?.livePosition ?? null,
            };
            next[key] = slot;
            changed = true;
        }

        // Anything the server no longer lists is closed. Keep a slot only while
        // this client is still mid-flight on it (submitting/closing), where the
        // list legitimately lags the local state.
        for (const [key, slot] of Object.entries(s.positionsByCoin)) {
            if (next[key]) continue;
            if (slot.status === 'submitting' || slot.status === 'closing') {
                next[key] = slot;
                continue;
            }
            changed = true;
        }

        if (!changed && sameSlotMap(s.positionsByCoin, next)) return;
        set(projectFrom(next, s.selectedCoin));
    },

    _syncExecution: (d) => {
        const s = get();
        const key = coinKey(d.coin) ?? s.selectedCoin;
        // No coin to file it under and no market selected: nothing to key on, and
        // writing it to a guessed slot would attribute the trade to the wrong
        // market. The projection stays as it was.
        if (key === null) return;

        const cur = s.positionsByCoin[key] ?? IDLE_SLOT;
        if (
            cur.status === d.status &&
            cur.phase === d.phase &&
            cur.tradeHash === d.tradeHash &&
            cur.openTxHash === d.openTxHash &&
            cur.positionType === d.positionType &&
            cur.positionAmount === d.positionAmount &&
            cur.entryPrice === d.entryPrice &&
            cur.leverage === d.leverage &&
            cur.limitPrice === (d.limitPrice ?? null) &&
            cur.openedAt === d.openedAt &&
            cur.fillMs === d.fillMs &&
            cur.openSource === d.openSource
        )
            return;

        const slot: PositionSlot = {
            status: d.status,
            phase: d.phase,
            tradeHash: d.tradeHash,
            openTxHash: d.openTxHash,
            positionType: d.positionType,
            positionAmount: d.positionAmount,
            entryPrice: d.entryPrice,
            leverage: d.leverage,
            limitPrice: d.limitPrice ?? null,
            openedAt: d.openedAt,
            fillMs: d.fillMs,
            openSource: d.openSource,
            // the exchange snapshot belongs to the market, not to the execution
            // attempt, so it survives a status transition on the same coin.
            livePosition: cur.livePosition,
        };
        set(
            projectFrom(withSlot(s.positionsByCoin, key, slot), s.selectedCoin),
        );
    },

    _syncBalance: (d) => {
        const s = get();
        if (
            s.balance === d.balance &&
            s.balanceForInputCap === d.balanceForInputCap &&
            s.custodialAvailable === d.custodialAvailable
        )
            return;
        // Restore the bet if it was clamped to 0 before balance arrived —
        // prefer the user's saved bet over the mode default.
        // quantized on the way in: this is the one path that writes betAmount
        // without going through a setter, and every consumer assumes cents.
        const savedBet = loadTradeConfig().betAmount;
        const preferredBet =
            typeof savedBet === 'number' && savedBet > 0
                ? floorToCents(savedBet)
                : INITIAL_BET_REAL;
        const restoreBet =
            s.betAmount === 0 &&
            s.runtimeMode === 'real' &&
            d.balanceForInputCap >= Math.min(preferredBet, INITIAL_BET_REAL);
        set({
            balance: d.balance,
            balanceForInputCap: d.balanceForInputCap,
            custodialAvailable: d.custodialAvailable,
            ...(restoreBet ? { betAmount: preferredBet } : {}),
        });
    },

    _syncCustodialBalance: (d) => {
        const s = get();
        if (
            s.custodialNumericBalance === d.numericBalance &&
            s.isCustodialBalanceLoading === d.isLoading &&
            s.isCustodialBalanceSettling === d.isSettling &&
            s.isCustodialBalanceProvisional === (d.isProvisional ?? false)
        )
            return;
        set({
            custodialNumericBalance: d.numericBalance,
            isCustodialBalanceLoading: d.isLoading,
            isCustodialBalanceSettling: d.isSettling,
            isCustodialBalanceProvisional: d.isProvisional ?? false,
        });
    },

    _syncLivePosition: (coin, snapshot) => {
        const s = get();
        const key = coinKey(coin) ?? s.selectedCoin;
        if (key === null) return;
        const slot = s.positionsByCoin[key] ?? IDLE_SLOT;
        const cur = slot.livePosition;
        const same =
            cur === snapshot ||
            (cur !== null &&
                snapshot !== null &&
                cur.entryPx === snapshot.entryPx &&
                cur.marginUsed === snapshot.marginUsed &&
                cur.leverage === snapshot.leverage &&
                cur.unrealizedPnl === snapshot.unrealizedPnl &&
                cur.returnOnEquity === snapshot.returnOnEquity &&
                cur.markPrice === snapshot.markPrice &&
                cur.size === snapshot.size &&
                cur.isLong === snapshot.isLong &&
                cur.liquidationPx === snapshot.liquidationPx);
        // Unchanged figures keep the EXISTING snapshot, anchor included. Re-stamping
        // the anchor on an identical poll would rebase the extrapolation to the
        // current price and silently discard the move since the real snapshot.
        //
        // Per coin, deliberately: coin B's figures moving must not re-anchor coin
        // A, or A's extrapolation restarts from a price its own PnL never saw.
        if (same) return;
        // Anchor ONLY against a price that is genuinely this market's. Two
        // conditions, and both are load-bearing:
        //
        //   key === selectedCoin      — an off-screen market is not streaming, so
        //                               there is no live price to anchor it to.
        //   currentValueCoin === key  — and even the charted market's price is
        //                               still the PREVIOUS market's for as long
        //                               as the spring takes to cross. Anchoring
        //                               there is what briefly rendered
        //                               -$27,855,852.53 (-279,189,353.66%) on a
        //                               $0.14 market: the anchor held ~$78,467.
        //
        // Unanchored is a safe state — realtimePnl degrades to the exchange's own
        // figure, which is correct, just not extrapolated between polls.
        const priceIsThisMarket =
            key === s.selectedCoin && s.currentValueCoin === key;
        const anchorPrice = priceIsThisMarket ? s.currentValue || null : null;
        const next: PositionSlot = {
            ...slot,
            livePosition:
                snapshot === null ? null : { ...snapshot, anchorPrice },
        };
        set(
            projectFrom(withSlot(s.positionsByCoin, key, next), s.selectedCoin),
        );
    },

    _syncIsReactivating: (isReactivating) => {
        if (get().isReactivating !== isReactivating) set({ isReactivating });
    },

    _syncHasPairData: (hasPairData) => {
        if (get().hasPairData !== hasPairData) set({ hasPairData });
    },
}));

// ---------------------------------------------------------------------------
// Derived selectors
// ---------------------------------------------------------------------------
// Plain functions usable with useTradingStore(selector).

export type { TradingState };

/** Renders a bound that is ALREADY cent-quantized (the callers all pass a
 *  selectEffective{Min,Max}Collateral result), so `toFixed` here is a pure
 *  formatter and cannot round the number away from the value the constraint
 *  check used — which is how "Maximum 50x collateral is $13.72" used to appear
 *  while $13.72 was being rejected. */
function formatCollateralUsd(value: number): string {
    return roundDisplayAmount(value).toFixed(DISPLAY_AMOUNT_PRECISION);
}

export type LeverageConstraintViolation =
    'below_min' | 'above_max' | 'unsatisfiable';

export const selectIsRealMode = (s: TradingState): boolean =>
    s.runtimeMode === 'real';

export const selectEffectiveMinCollateral = (s: TradingState): number | null =>
    s.leverageMinCollateralUsd === null
        ? null
        : ceilToCents(s.leverageMinCollateralUsd);

// delegates rather than restating the derivation: the two used to be written
// out twice, which is exactly how a bound and the copy describing it drift.
export const selectEffectiveMaxCollateral = (s: TradingState): number | null =>
    getEffectiveMaxCollateral(s.balanceForInputCap, s.leverageMaxCollateralUsd);

export const selectLeverageConstraintViolation = (
    s: TradingState,
): LeverageConstraintViolation | null => {
    // both bounds come from the selectors, i.e. already cent-quantized in the
    // safe direction — comparing against the raw store fields would reintroduce
    // the sub-cent mismatch the messages below cannot express.
    const effectiveMin = selectEffectiveMinCollateral(s);
    const effectiveMax = selectEffectiveMaxCollateral(s);
    if (
        effectiveMin !== null &&
        effectiveMax !== null &&
        effectiveMin > effectiveMax
    ) {
        return 'unsatisfiable';
    }

    if (
        s.betAmount > 0 &&
        effectiveMin !== null &&
        s.betAmount < effectiveMin
    ) {
        return 'below_min';
    }

    if (effectiveMax !== null && s.betAmount > effectiveMax) {
        return 'above_max';
    }

    return null;
};

export const selectIsBetWithinLeverageConstraints = (
    s: TradingState,
): boolean => selectLeverageConstraintViolation(s) === null;

export const selectLeverageConstraintMessage = (
    s: TradingState,
): string | null => {
    const violation = selectLeverageConstraintViolation(s);
    const effectiveMin = selectEffectiveMinCollateral(s);
    const effectiveMax = selectEffectiveMaxCollateral(s);

    if (
        violation === 'unsatisfiable' &&
        effectiveMin !== null &&
        effectiveMax !== null
    ) {
        return `At ${s.leverage}x, minimum collateral is $${formatCollateralUsd(
            effectiveMin,
        )}, but the current max is $${formatCollateralUsd(effectiveMax)}.`;
    }

    if (violation === 'below_min' && effectiveMin !== null) {
        return `Minimum ${s.leverage}x collateral is $${formatCollateralUsd(
            effectiveMin,
        )}.`;
    }

    if (violation === 'above_max' && effectiveMax !== null) {
        return `Maximum ${s.leverage}x collateral is $${formatCollateralUsd(
            effectiveMax,
        )}.`;
    }

    return null;
};

interface ActiveTradeVisualCache {
    tradeHash: string | null;
    openTxHash: string | null;
    executionStatus: TradeExecutionStatus;
    positionType: PositionType;
    positionAmount: number;
    entryPrice: number;
    leverage: number;
    openedAt: number | null;
    /** identity-compared: _syncLivePosition only replaces it on a real change. */
    livePosition: LivePositionSnapshot | null;
    value: ActivePosition;
}

/**
 * Cache real-mode visual positions so selector snapshots stay referentially
 * stable between unchanged store reads (required by useSyncExternalStore).
 *
 * Keyed by coin. A single cache object was correct while only one position could
 * exist; with several, every switch between markets evicted the other's entry
 * and rebuilt a fresh object on the way back — re-rendering the chart, the rail
 * and the table on each flip of the chart, forever.
 */
const activeTradeVisualCache = new Map<string, ActiveTradeVisualCache>();

/** Bounded so a session that browses hundreds of markets cannot grow it without
 *  limit. Far above any realistic number of concurrent positions, so the entries
 *  that matter are never the ones evicted. */
const VISUAL_CACHE_LIMIT = 32;

function rememberVisual(key: string, entry: ActiveTradeVisualCache): void {
    if (
        !activeTradeVisualCache.has(key) &&
        activeTradeVisualCache.size >= VISUAL_CACHE_LIMIT
    ) {
        const oldest = activeTradeVisualCache.keys().next().value;
        if (oldest !== undefined) activeTradeVisualCache.delete(oldest);
    }
    activeTradeVisualCache.set(key, entry);
}

// True while a request is in flight (submitting an open or a close) — buttons disabled.
export const selectExecutionInFlight = (s: TradingState): boolean =>
    s.runtimeMode === 'real' &&
    (s.executionStatus === 'submitting' || s.executionStatus === 'closing');

// true when there's a live/pending/closing position (something to show).
export const selectHasRealOpenTrade = (s: TradingState): boolean =>
    s.runtimeMode === 'real' &&
    (s.executionStatus === 'pending' ||
        s.executionStatus === 'open' ||
        s.executionStatus === 'closing');

/**
 * A GTC limit order is sitting on the book for the charted market.
 *
 * Deliberately NOT part of `selectHasRealOpenTrade`: there is no position, no
 * PnL and nothing to close, so every consumer of that selector (the close view,
 * the live-PnL headline, the close button) must keep saying no. What a resting
 * order DOES do is hold the pair's slot, which is why `selectCanOpen` is false
 * while it stands.
 *
 * Demo resting orders are the same thing without a backend, so both count.
 */
export const selectHasRestingOrder = (s: TradingState): boolean =>
    s.runtimeMode === 'real'
        ? s.executionStatus === 'resting'
        : s.selectedCoin !== null &&
          s.demoRestingOrders[s.selectedCoin] !== undefined;

// Unified trade visual: normalises demo activePosition and real-mode execution state into a single ActivePosition for chart/panel rendering.
export const selectActiveTradeVisual = (
    s: TradingState,
): ActivePosition | null => {
    if (s.runtimeMode !== 'real') {
        activeTradeVisualCache.clear();
        return s.activePosition;
    }

    // The projection is the selected market's slot, so that is the cache key.
    const cacheKey = s.selectedCoin;
    if (cacheKey === null) return null;

    if (
        selectHasRealOpenTrade(s) &&
        s.execPositionType &&
        s.execPositionAmount !== null &&
        s.execEntryPrice !== null
    ) {
        // The exchange wins on every figure it reports. The execution state is
        // what the CLIENT asked for and captured, which is a different number:
        // its entry is the fill we saw rather than the size-weighted average,
        // and its amount is the collateral requested rather than marginUsed.
        const lp = s.livePosition;
        const leverage = lp?.leverage ?? s.execLeverage ?? 1;
        const entryPrice = lp?.entryPx ?? s.execEntryPrice;
        const amount = lp?.marginUsed ?? s.execPositionAmount;
        const cache = activeTradeVisualCache.get(cacheKey);
        if (
            cache &&
            cache.tradeHash === s.tradeHash &&
            cache.openTxHash === s.openTxHash &&
            cache.executionStatus === s.executionStatus &&
            cache.positionType === s.execPositionType &&
            cache.positionAmount === amount &&
            cache.entryPrice === entryPrice &&
            cache.leverage === leverage &&
            cache.openedAt === s.execOpenedAt &&
            cache.livePosition === lp
        ) {
            return cache.value;
        }

        const nextValue: ActivePosition = {
            type: s.execPositionType,
            entryPrice,
            amount,
            pnlValue: lp?.unrealizedPnl ?? null,
            pnlPercent: lp === null ? null : exchangePnlPercent(lp),
            markPrice: lp?.markPrice ?? null,
            pnlAnchorPrice: lp?.anchorPrice ?? null,
            size: lp?.size ?? null,
            // The execution state owns the open time (accept time for a live
            // open, backend createdAt for a restored one). Stamping Date.now()
            // here made every cache rebuild — status transition, entry
            // refinement, post-refresh hydration — re-date the position to the
            // present. Date.now() survives only as a last-resort fallback.
            timestamp: s.execOpenedAt ?? Date.now(),
            leverage,
        };

        rememberVisual(cacheKey, {
            tradeHash: s.tradeHash,
            openTxHash: s.openTxHash,
            executionStatus: s.executionStatus,
            positionType: s.execPositionType,
            positionAmount: amount,
            entryPrice,
            leverage,
            openedAt: s.execOpenedAt,
            livePosition: lp,
            value: nextValue,
        });

        return nextValue;
    }

    activeTradeVisualCache.delete(cacheKey);
    return null;
};

/**
 * True when the visible position was restored (reload / reconciliation /
 * snapshot resume) rather than opened in this session. Consumers use it to
 * stay silent about a "fill" that already happened. Demo positions are always
 * opened locally, so they are never restored.
 */
export const selectActiveTradeRestored = (s: TradingState): boolean =>
    s.runtimeMode === 'real' && s.execOpenSource === 'restored';

/** Measured request→fill latency of the live position; null in demo mode. */
export const selectActiveTradeFillMs = (s: TradingState): number | null =>
    s.runtimeMode === 'real' ? s.execFillMs : null;

/**
 * True while a real balance is on screen but trading authority has not resolved
 * yet (post-reload, ~10-15s). Deliberately NOT gated on real mode: the flag is
 * only ever set while `runtimeMode` is still 'demo' (see Trading.tsx, where it
 * is `hasAppToken && !isRealMode && the balance read landed`). That is exactly
 * the window in which a Long/Short click took the demo branch and opened a demo
 * position sized against the real balance, which the mode-switch reset then
 * wiped without explanation.
 *
 * An honest demo user has no app token, so the balance query never runs and this
 * stays false — their trading is untouched.
 */
export const selectIsAuthorityResolving = (s: TradingState): boolean =>
    s.isCustodialBalanceProvisional;

export const selectIsTradeDisabled = (s: TradingState): boolean =>
    (s.runtimeMode === 'real' && !s.hasPairData) ||
    selectIsAuthorityResolving(s) ||
    selectExecutionInFlight(s) ||
    // an open placed mid-reactivation races the agent approval it depends on, so hold every
    // entry point — this selector also gates the L/S keyboard shortcuts, not just the buttons.
    (s.runtimeMode === 'real' && s.isReactivating) ||
    (s.runtimeMode === 'real' && s.resumeUnverified);

/**
 * The selectIsTradeDisabled causes that had NO visible explanation under the
 * CTAs: without one, a disabled Long/Short reads as a broken button. The
 * in-flight and resume causes already render their own rows, so they are left
 * out here. Precedence matches the selector: missing pair data first (nothing
 * can be traded at all), then the unresolved authority (transient, resolves on
 * its own), then a running reactivation.
 */
export const selectTradeDisabledReason = (
    s: TradingState,
): 'pairData' | 'authorityResolving' | 'reactivating' | null => {
    if (s.runtimeMode === 'real' && !s.hasPairData) return 'pairData';
    // checked before the real-mode gate below: this is the one reason that fires
    // while runtimeMode is still 'demo'.
    if (selectIsAuthorityResolving(s)) return 'authorityResolving';
    if (s.runtimeMode !== 'real') return null;
    if (s.isReactivating) return 'reactivating';
    return null;
};

export const selectCanAfford = (s: TradingState): boolean =>
    s.betAmount > 0 &&
    selectIsBetWithinLeverageConstraints(s) &&
    s.balance >= s.betAmount;

// shortfall vs the user's in-system balance — the amount they'd need to deposit.
export const selectCustodialShortfall = (s: TradingState): number =>
    s.runtimeMode === 'real'
        ? Math.max(0, s.betAmount - s.custodialAvailable)
        : 0;

/** Gate the banner on a DEPOSITABLE gap, not on `> 0`. Cent quantization makes
 *  a sub-cent shortfall unreachable through the store's setters, so this is a
 *  backstop: it only decides whether to render the banner, never whether the
 *  trade is affordable (selectCanAfford stays exact), so it cannot let an
 *  unaffordable order through — it just stops the UI from ever asking for
 *  "$0.00 more" again. */
export const selectHasCustodialWarning = (s: TradingState): boolean =>
    selectCustodialShortfall(s) >= MIN_MEANINGFUL_USD;

export const selectShowZeroCustodialPrompt = (s: TradingState): boolean =>
    s.runtimeMode === 'real' &&
    s.custodialAvailable === 0 &&
    !s.isCustodialBalanceLoading &&
    !selectActiveTradeVisual(s);

export const selectCanClose = (s: TradingState): boolean =>
    s.runtimeMode === 'real'
        ? s.executionStatus === 'open'
        : !!s.activePosition;

// A resting order holds the pair's slot, so neither branch can open: the
// backend refuses a second order on the same pair, and in demo the one local
// position is already spoken for. This also gates the L/S keyboard shortcuts.
export const selectCanOpen = (s: TradingState): boolean =>
    s.runtimeMode === 'real'
        ? (s.executionStatus === 'idle' || s.executionStatus === 'failed') &&
          !selectIsTradeDisabled(s) &&
          selectCanAfford(s)
        : !s.activePosition && !selectHasRestingOrder(s) && selectCanAfford(s);

// ---------------------------------------------------------------------------
// Aggregate reads across every open market
// ---------------------------------------------------------------------------
// With one position these were all the same question as "the position". With
// several they are not, and Option A ("the rail follows the chart") makes them
// load-bearing: the market going wrong may not be the one on screen.

/**
 * The market a trade belongs to, and its slot — found by the trade's OWN hash.
 *
 * The alternative everything used to do was compare against `s.tradeHash`, which
 * is the projection, i.e. the charted market's trade. That silently dropped every
 * event for every other market: a stop-loss on ETH while the user watched BTC
 * matched nothing and produced no card, no toast and no refetch.
 */
/**
 * The live price, but only when it is genuinely the charted market's.
 *
 * Anything that extrapolates a position's PnL between polls must price it off
 * this rather than raw `currentValue`. The anchor guard in `_syncLivePosition`
 * stops a BAD anchor being stamped; this stops a good anchor being measured
 * against the previous market's price while the spring is still crossing —
 * the other half of the same bug, and the half that actually rendered
 * -279,189,353.66%.
 *
 * `realtimePnl` degrades to the exchange's own figure on a null, which is the
 * correct thing to show for those few hundred milliseconds.
 */
export const selectChartedLivePrice = (s: TradingState): number | null =>
    s.selectedCoin !== null &&
    s.currentValueCoin === s.selectedCoin &&
    s.currentValue > 0
        ? s.currentValue
        : null;

/**
 * One market's exchange snapshot, or null.
 *
 * The positions table needs this per row: every market the user holds shows its
 * own live PnL, not just the one on the chart. Before the bulk position read
 * existed only the charted market had figures, so the other rows rendered "—"
 * — which reads as "no PnL" when it actually meant "not fetched".
 */
export const selectLivePositionFor =
    (coin: string | null | undefined) =>
    (s: TradingState): LivePositionSnapshot | null => {
        const key = coinKey(coin);
        return key === null
            ? null
            : (s.positionsByCoin[key]?.livePosition ?? null);
    };

export function findSlotByTradeHash(
    s: TradingState,
    tradeHash: string,
): { coin: string; slot: PositionSlot } | null {
    for (const [coin, slot] of Object.entries(s.positionsByCoin)) {
        if (slot.tradeHash === tradeHash) return { coin, slot };
    }
    return null;
}

const NO_HELD_COINS: ReadonlySet<string> = new Set();

/**
 * The upper-cased coins this client holds a position on.
 *
 * Used by the market pickers to MARK a market rather than disable it. Switching
 * markets used to be locked outright while a position was open — correct when
 * only one position could exist, since leaving its market stranded it. With one
 * position per coin that lock made the feature unreachable: every route to a
 * second market went through a picker that refused to move.
 *
 * Reference-stable on purpose: it is read through useSyncExternalStore, and a
 * fresh Set per call is never Object.is-equal to the last one, so the reader
 * re-renders, re-selects, and loops forever.
 */
let heldCoinsCache: {
    source: Record<string, PositionSlot>;
    value: ReadonlySet<string>;
} | null = null;

export const selectHeldCoins = (s: TradingState): ReadonlySet<string> => {
    if (s.runtimeMode !== 'real') return NO_HELD_COINS;
    if (heldCoinsCache?.source === s.positionsByCoin)
        return heldCoinsCache.value;
    const keys = Object.entries(s.positionsByCoin)
        // a resting order marks the market too: opening a second order there
        // would be refused, so the picker must show it as taken.
        .filter(
            ([, slot]) =>
                slot.status === 'open' ||
                slot.status === 'pending' ||
                slot.status === 'resting',
        )
        .map(([coin]) => coin);
    const value = keys.length === 0 ? NO_HELD_COINS : new Set(keys);
    heldCoinsCache = { source: s.positionsByCoin, value };
    return value;
};

/**
 * Total margin committed across every open market.
 *
 * Isolated margin caps the blast radius per position, but nothing stopped a user
 * committing their whole balance across five markets without ever seeing a
 * total — the per-position figures are each small and the sum was never shown.
 */
export const selectTotalMarginUsed = (s: TradingState): number => {
    if (s.runtimeMode !== 'real') return 0;
    let total = 0;
    for (const slot of Object.values(s.positionsByCoin)) {
        // the exchange's marginUsed where we have it, the requested collateral
        // otherwise — the same precedence selectActiveTradeVisual uses.
        const margin = slot.livePosition?.marginUsed ?? slot.positionAmount;
        if (margin !== null) total += margin;
    }
    return total;
};

/** Unrealized PnL summed across every open market, or null when no market has
 *  reported one yet (so callers can tell "flat" from "not loaded"). */
export const selectTotalUnrealizedPnl = (s: TradingState): number | null => {
    if (s.runtimeMode !== 'real') return null;
    let total = 0;
    let any = false;
    for (const slot of Object.values(s.positionsByCoin)) {
        const pnl = slot.livePosition?.unrealizedPnl ?? null;
        if (pnl === null) continue;
        any = true;
        total += pnl;
    }
    return any ? total : null;
};

/** How many markets are currently held. A resting order is NOT a position:
 *  nothing is at risk until it fills, so it must not be counted as one. */
export const selectOpenPositionCount = (s: TradingState): number =>
    s.runtimeMode === 'real'
        ? Object.values(s.positionsByCoin).filter(
              (slot) => slot.status === 'open' || slot.status === 'pending',
          ).length
        : s.activePosition
          ? 1
          : 0;

export const selectCanAdjustBet = (s: TradingState): boolean =>
    s.runtimeMode === 'real' || !s.activePosition;

// Real-mode-aware execution status for components: undefined in demo mode.
export const selectExecutionStatus = (
    s: TradingState,
): TradeExecutionStatus | undefined =>
    s.runtimeMode === 'real' ? s.executionStatus : undefined;
